import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AcceptAccountantInvite() {
  const { acceptAccountantInvite } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("accountant_invalid_link"));
      return;
    }
    api.get("/accountant/invite-info", { params: { token } })
      .then((res) => setInfo(res.data))
      .catch(() => setError(t("accountant_invalid_link")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await acceptAccountantInvite({ token, name: info.account_exists ? undefined : name, password });
      navigate("/accountant/clients");
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <Compass className="h-6 w-6 text-[hsl(var(--success))]" />
          <span className="font-display text-2xl">YieldPilot</span>
        </div>

        {error && <p className="text-sm text-[hsl(var(--destructive))]" data-testid="accept-invite-error">{error}</p>}

        {!error && !info && <p className="text-sm text-muted-foreground">{t("loading")}</p>}

        {!error && info && (
          <>
            <h1 className="font-display text-3xl mb-1">{t("accountant_accept_title")}</h1>
            <p className="text-muted-foreground text-sm mb-8">
              {info.owner_name} {t("accountant_accept_sub")}
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
              <div className="space-y-2">
                <Label>{t("email")}</Label>
                <Input value={info.invited_email} disabled className="rounded-sm" />
              </div>
              {!info.account_exists && (
                <div className="space-y-2">
                  <Label htmlFor="accept-name">{t("name")}</Label>
                  <Input id="accept-name" required value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="accept-invite-name" className="rounded-sm" />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="accept-password">{t("password")}</Label>
                <Input id="accept-password" type="password" required minLength={6} value={password}
                  onChange={(e) => setPassword(e.target.value)} data-testid="accept-invite-password" className="rounded-sm" />
              </div>
              <Button type="submit" disabled={loading} data-testid="accept-invite-submit"
                className="w-full rounded-sm active:scale-95 transition-transform">
                {loading ? "..." : t("accountant_accept_btn")}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
